import React, { useEffect, useState, useMemo } from 'react'
import { modifiedColumns } from './Column'
import { terminalView } from '@fareye/configs'
import { Flex, styled, Table } from '@fareye/ui'
const DynamicPaginationContainer: any = styled(Flex)`
  padding-top: 8px;
  padding-bottom: 8px;
  font-size: 16px;
  font-weight: 500;
  background: ${props =>
    props.title == 'ARRIVALS'
      ? terminalView.darkTheme.arrival.paginationHeader
      : terminalView.darkTheme.departure.paginationHeader};
  @media (min-width: 3840px) {
    padding-top: 20px;
    padding-bottom: 20px;
    font-size: 44px;
    font-weight: 600;
  }
`

function getColor(id: any, tableType: any) {
  const tableTypeColor =
    tableType == 'ARRIVALS'
      ? terminalView.darkTheme.arrival
      : terminalView.darkTheme.departure
  const color = id % 2 == 0 ? tableTypeColor.row1 : tableTypeColor.row2
  return color
}

function sortAlpha(dir: boolean, a: string, b: string) {
  return dir
    ? a?.toLowerCase() < b?.toLowerCase()
      ? 1
      : -1
    : b?.toLowerCase() < a?.toLowerCase()
    ? 1
    : -1
}

function sortDate(dir: boolean, a: string, b: string) {
  let tempSort = new Date(a).getTime() - new Date(b).getTime()
  return dir ? tempSort : -1 * tempSort
}

export const TerminalViewTable = (props: any) => {
  let timer: any
  const [pageIndex, setPageIndex] = useState(0)
  const [sorting, setSorting] = useState({ sortBy: '', sortByDirection: true })
  const [sortedData, setSortedData] = useState(props.fetchedDataFromApi)

  let totalPages = props.pageSize
    ? Math.ceil(props.fetchedDataFromApi.length / props.pageSize)
    : 0

  useEffect(() => {
    let x = pageIndex
    timer = setInterval(() => {
      x = totalPages ? (x + 1) % totalPages : 0
      setPageIndex(x)
    }, props.pageChangeTimeInterval)
    return () => clearInterval(timer)
  })

  // Update sorted data when fetched data or sorting changes
  useEffect(() => {
    if (!sorting.sortBy) {
      setSortedData(props.fetchedDataFromApi)
      return
    }

    const { sortBy, sortByDirection } = sorting
    const dataToSort = [...props.fetchedDataFromApi]

    const sorted = dataToSort.sort((a: any, b: any) => {
      const aVal = a[sortBy]
      const bVal = b[sortBy]

      // Handle date fields (check if formatter is dateFormatter)
      const columnConfig = props.columns.find((col: any) => col.key === sortBy)
      if (columnConfig?.formatter === 'dateFormatter') {
        return sortDate(sortByDirection, aVal, bVal)
      }

      // Handle string fields
      if (typeof aVal === 'string' || typeof bVal === 'string') {
        return sortAlpha(sortByDirection, aVal || '', bVal || '')
      }

      // Handle numeric fields
      const aNum = parseFloat(aVal) || 0
      const bNum = parseFloat(bVal) || 0
      return sortByDirection ? aNum - bNum : bNum - aNum
    })

    setSortedData(sorted)
  }, [props.fetchedDataFromApi, sorting])

  const tableColorType =
    props.tableType == 'ARRIVALS'
      ? terminalView.darkTheme.arrival
      : terminalView.darkTheme.departure

  const columns = useMemo(
    () => modifiedColumns(props.tableName, props.columns),
    [props.tableName, props.columns]
  )

  const tableProps = {
    data: sortedData,
    columns: columns,
    totalElements: sortedData.length,
    defaultPageSize: props.pageSize,
    dataLoading: props.dataLoading,
    pageChangeTimeInterval: props.pageChangeTimeInterval,
    style: {
      th: {
        background: tableColorType.header,
        color: tableColorType.headerText,
        borderRight: '0px',
        padding: '0px'
      },
      table: {
        border: '0px'
      }
    },
    getTrProps: (row: any) => {
      return {
        style: {
          backgroundColor: getColor(row.id, props.tableType)
        }
      }
    },
    getTdProps: (row: any) => {
      return {
        style: {
          padding: '0px',
          borderRight: '0px'
        }
      }
    }
  }

  const paginationContainer = ({ children }: any) => {
    return (
      <DynamicPaginationContainer alignItemsCenter title={props.tableType}>
        <div
          style={{
            marginLeft: '10px',
            marginRight: 'auto',
            padding: '5px 0px',
            color: terminalView.darkTheme.arrival.paginationText
          }}
        >
          {props.tableName}
        </div>
        {children}
      </DynamicPaginationContainer>
    )
  }

  const hasSortableColumns = columns.some((col: any) => col.sortable)

  return (
    <Table
      {...tableProps}
      data={sortedData}
      defaultPageSize={props.pageSize}
      showTotalCount={false}
      showTerminalView={true}
      paginationWrapper={paginationContainer}
      defaultPageIndex={pageIndex}
      sortable={hasSortableColumns}
      onSortedChange={(ev: any) => {
        if (ev && ev[0] && ev[0].id) {
          const columnId = ev[0].id
          const column = columns.find((col: any) => col.id === columnId)
          if (column?.sortable) {
            setSorting({
              sortBy: columnId,
              sortByDirection:
                columnId === sorting.sortBy ? !sorting.sortByDirection : ev[0].desc
            })
          }
        }
      }}
    />
  )
}
